import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    signal
} from '@angular/core';
import { Router } from '@angular/router';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { ButtonColor, ButtonSize, ButtonType } from '@predictiveindex/tapestry';
import { Subject, takeUntil } from 'rxjs';
import { PI_VERSION, PrivacyLink } from '../../constants-types';
import { IconSize, IconType, Person } from '../../core-data-model';
import { AuthenticationService, CompanySettingsApi } from '../../core-services';
import {
    FeatureFlags,
    FeatureFlagsService,
    InitDataService,
    ModuleUserAccessService,
    UserProfileSettingsService
} from '../../system-services';
import { TranslationService } from '../../translations';
import { MyBaService } from '../my-ba-profile/my-ba.service';
import { MyProfileTooltipService } from '../profile-tooltip/my-profile-tooltip.service';

// Update to Tapestry Tabs and Translations
export enum profileTabs {
    PROFILE = 'My Behaviors',
    AVATARSANDNAME = 'My Info'
}
@Component({
    selector: 'my-profile',
    templateUrl: 'my-profile.component.html',
    styleUrls: ['./my-profile.component.less'],
    standalone: false
})
export class MyProfileComponent implements OnInit, OnDestroy {
    @Input()
    isMobileMenu = false;
    @Output()
    dismissMyProfile: EventEmitter<void> = new EventEmitter();

    isLoading = true;
    isThirdPartyUser: boolean;
    IconType = IconType;
    IconSize = IconSize;
    ButtonSize = ButtonSize;
    ButtonColor = ButtonColor;
    ButtonType = ButtonType;
    buttonIcon = 'edit';
    editable = false;
    userName = '';

    // List of tabs
    navTabs = [profileTabs.PROFILE];
    currentTab: profileTabs = profileTabs.PROFILE; // We start with My Profile tab as the default
    PrivacyLink = PrivacyLink;
    piVersionKey = 'footer.version';
    piVersion = PI_VERSION;
    profileTabs = profileTabs;
    showAvatar = false;

    enableEditing = false;

    enableMyFiles = signal(false);

    private destroyed$ = new Subject<void>();

    constructor(
        public readonly initData: InitDataService,
        public readonly TR: TranslationService,
        private readonly myProfileTooltipService: MyProfileTooltipService,
        private readonly myBAService: MyBaService,
        private readonly userProfileSettingsService: UserProfileSettingsService,
        private readonly auth: AuthenticationService,
        private readonly companySettingsApi: CompanySettingsApi,
        private readonly router: Router,
        private readonly featureFlags: FeatureFlagsService,
        private readonly moduleUserAccess: ModuleUserAccessService
    ) {}

    get self(): Person {
        return this.myProfileTooltipService.person;
    }

    get showMe(): boolean {
        return !!this.self?.referencePattern;
    }

    get showTabNavigation() {
        return (
            this.myBAService.hasViewedProfileCover() ||
            !this.self?.referencePattern
        );
    }

    async ngOnInit() {
        this.loadTabOptions();
        this.userName = this.initData.user?.fullName();

        await this.myProfileTooltipService.determineAssessmentState();
        this.isThirdPartyUser =
            !this.userProfileSettingsService.isUserLoggedInDefaultCompany();

        this.enableMyFiles.set(
            this.moduleUserAccess.hasAccessLevelForAnyModule(
                PermissionsModuleAccessLevel.AllModuleAdmin
            ) && this.featureFlags.getByFlag(FeatureFlags.enableDownloadCSV)
        );
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    toggleEditableFields(): void {
        this.currentTab = profileTabs.AVATARSANDNAME;
        this.editable = !this.editable;
    }

    loadTabOptions() {
        this.companySettingsApi
            .fetchPersonalizationSettings()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.showAvatar = false;
            });
    }

    captureClickHandler(event: MouseEvent) {
        // To bypass the default behavior of mat-menu dismissing the contents once a click is registered.
        event.stopPropagation();
        event.preventDefault();
    }

    navigateToTabSection(selectedNavChoice: profileTabs): void {
        this.currentTab = selectedNavChoice;
    }

    highlightTab(navTab: profileTabs): boolean {
        return navTab === this.currentTab;
    }

    logout() {
        this.auth.logout();
    }

    userNameChanged(name: string) {
        this.userName = name;
    }

    async navigateToMyFiles() {
        await this.router.navigate(['profile', 'files']);
        this.dismissMyProfile.emit();
    }
}
